import {Capability, DeviceTypeSetLimit} from "@/api";
import {Form} from "@/utils/Form";
import ErrorHelper from "@/utils/ErrorHelper";
import {Error} from "@/api";
import deepEqual from "deep-equal";
import {
    UpdateExtensionLimitMutation,
    UpdateExtensionLimitMutationVariables,
    useUpdateExtensionLimitMutation,
} from "./queries.generated";
import {Limit, LimitErrorType} from "../types";
import {showNotification} from "@/actions";
import {defineMessage} from "react-intl";
import {CapabilitiesThatCanBeUsed, UNLIMITED} from "@/constants";
import {validateCapabilitySet, validateUsedCapability} from "../tools";
import {createInitialData, DataSource} from "./DataSource";
import {DeviceType} from "../devices/utils";

export type LimitError =
    | {
          type: "devices_needed";

          devices: {name: string; needed: number}[];
      }
    | {
          type: "supervisor" | "agent";

          groups: string[];
      };

export default class UpdateExtensionLimitForm extends Form<
    UpdateExtensionLimitMutation,
    UpdateExtensionLimitMutationVariables
> {
    private readonly id: number;
    readonly dataSource: DataSource;
    readonly isSuperuser: boolean;

    constructor(id: number, dataSource: DataSource, isSuperuser: boolean) {
        super("updateUserLimit");
        this.hooks(useUpdateExtensionLimitMutation, createInitialData(id, dataSource.limitSettings));
        this.id = id;
        this.dataSource = dataSource;
        this.isSuperuser = isSuperuser;
    }

    setDeviceTypeSetQuota(name: string, value: number) {
        console.log("Setting device type set quota", name, value);
        const values = structuredClone(this.values.deviceTypeSets);
        const set = values.filter((s) => s.name === name)[0];

        if (set) set.value = value;
        else values.push({name, value});

        this.updateValues({deviceTypeSets: values});
    }

    get capabilityLimits(): Limit[] {
        return (
            this.dataSource.limitSettings.domainAllocation?.capabilitySets?.map((cap) => {
                const limit = {
                    form: this.values.capabilitySets?.some((c) => c === cap.name) ? 1 : 0,
                    name: cap.name,
                    up: this.dataSource.limitSettings.userPackages
                        ?.find((p) => p.name === this.values.userPackageName)
                        ?.capabilitySets.some((c) => c === cap.name)
                        ? 1
                        : 0,
                    user: {
                        allocated: this.dataSource.limitSettings.userQuotaInfo.capabilitySets.includes(cap.name)
                            ? 1
                            : 0,
                        used: 0,
                    },
                    system: {
                        allocated: this.dataSource.superuserInformation?.totalCapabilityUsage[cap.name] ?? 0,
                        limit: cap.systemLimit,
                    },
                    domain: {
                        used: cap.domainUsed,
                        allocated: cap.domainAllocated,
                    },
                } as Limit;

                // Only go through switch statement if the capability can be used
                if (CapabilitiesThatCanBeUsed.map((c) => c.toLowerCase()).includes(cap.name))
                    switch (cap.name) {
                        // Agent is used if extension in any call group
                        case Capability.CcAgent.toLowerCase():
                            limit.user.used = this.agentIsUsed ? 1 : 0;
                            break;
                        // Supervisor is used if 'controlling_line' is true in a call group
                        case Capability.CcSupervisor.toLowerCase():
                            limit.user.used = this.supervisorIsUsed ? 1 : 0;
                            break;
                        default:
                            // In case we add a new capability that can be used
                            console.error("Unhandled capability set name: " + cap.name);
                            break;
                    }

                return limit;
            }) ?? []
        );
    }

    get agentIsUsed(): boolean {
        return this.dataSource.extensionCallQueues.length > 0;
    }

    get supervisorIsUsed(): boolean {
        return this.dataSource.extensionCallQueues.map((g) => g.controllingLine).includes(true);
    }

    // Validate a single capability, and handle its errors
    validateCapabilitySet(cap: Limit): LimitError[] {
        // HACK: this used the 'old' LimitError type, but that is overengineered and not used anymore
        // so we need to convert it to the new type
        const errors = validateCapabilitySet(cap, this.isSuperuser);

        return errors.map((error) => ({
            type: error.type === LimitErrorType.AGENT ? "agent" : "supervisor",
            groups: error.groups,
        }));
    }

    isCapabilityUsed(cap: Capability) {
        // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
        switch (cap) {
            case Capability.CcAgent:
                return this.agentIsUsed;
            case Capability.CcSupervisor:
                return this.supervisorIsUsed;
            default:
                return false;
        }
    }

    // Returns the currently selected capabilities from user package and additional ones
    get allowedCapabilities(): Capability[] {
        return [
            ...new Set(
                //this.dataSource.capabilitySets
                this.dataSource.capabilitySets
                    .filter((set) => this.values.capabilitySets.includes(set.name))
                    .concat([
                        this.dataSource.baseCapabilitySet,
                        ...this.dataSource.capabilitySetsInUserPackage(this.values.userPackageName),
                    ])
                    .flatMap((set) => set.capabilities.map((c) => c.toUpperCase() as Capability)),
            ),
        ];
    }

    // Validate the capabilities that can be used (agent, supervisor for now)
    validateUsableCap(cap: Capability): LimitError | null {
        // HACK: same as validateCapabilitySet
        const error = validateUsedCapability(
            cap,
            this.isCapabilityUsed(cap),
            this.allowedCapabilities,
            this.dataSource.extensionCallQueues,
        );

        return error
            ? {type: error.type === LimitErrorType.AGENT ? "agent" : "supervisor", groups: error.groups}
            : null;
    }

    // Combine form and package values and validate them
    validateCapabilities(): LimitError[] {
        const errors = [];
        for (const cl of this.capabilityLimits) {
            errors.push(...this.validateCapabilitySet(cl));
        }

        for (const cap of CapabilitiesThatCanBeUsed) {
            const error = this.validateUsableCap(cap);
            if (error) errors.push(error);
        }

        return errors;
    }

    validateDevices(): LimitError | undefined {
        const deviceMinimums = this.getDeviceMinimums();
        const errorDeviceTypes: {name: string; needed: number}[] = [];

        for (const [device, needed] of Object.entries(deviceMinimums)) {
            if (needed > 0) {
                errorDeviceTypes.push({name: device, needed});
            }
        }

        if (errorDeviceTypes.length > 0) return {type: "devices_needed", devices: errorDeviceTypes};
        else return undefined;
    }

    // Run validators
    validate(): LimitError[] {
        // TODO: should not validate agent and supervisor this way
        // it is pretty easy to just disable the checkboxes
        const errors = this.validateCapabilities();

        const devicesError = this.validateDevices();
        if (devicesError !== undefined) errors.push(devicesError);

        return errors;
    }

    get capabilitySetsFromUserPackage(): string[] {
        return (
            this.dataSource.limitSettings.userPackages.find((p) => p.name === this.values.userPackageName)
                ?.capabilitySets ?? []
        );
    }

    override async submit(): Promise<boolean> {
        const res = await super.submit();
        if (res) {
            this.clearCache();
            this.newInitialData(createInitialData(this.id, await this.dataSource.refetch()));
        } else {
            this.dispatch(
                showNotification(defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}), "error"),
            );
        }
        return res;
    }

    protected override get filteredVariables(): UpdateExtensionLimitMutationVariables {
        const values = structuredClone(this.values);
        const package_unlimited_dts = this.dataSource.limitSettings.userPackages
            ?.find((p) => p.name === values.userPackageName)
            ?.deviceTypeSets?.filter((d) => d.quantity == UNLIMITED)
            .map((d) => d.name);

        // Set the value of unlimited devices to 0 in form
        // to avoid visual bugs after submitting
        for (const dts of values.deviceTypeSets) {
            if (package_unlimited_dts.includes(dts.name)) {
                dts.value = 0;
            }
        }

        if (!values.capabilitySets) {
            delete values.capabilitySets;
        }
        if (!values.deviceTypeSets) {
            delete values.deviceTypeSets;
        }

        return values;
    }

    getDeviceMinimums(options?: {withFormDts?: DeviceTypeSetLimit[]; withFormUserPackageName?: string}) {
        const userPackageName = options?.withFormUserPackageName ?? this.values.userPackageName;
        const userPackage = this.dataSource.limitSettings.userPackages.find((p) => p.name === userPackageName);
        const userPackageSets = userPackage.deviceTypeSets;

        const result = structuredClone(this.dataSource.deviceUsage);

        for (const setQuota of userPackageSets) {
            if (setQuota.quantity === 0) continue;
            const set = this.dataSource.limitSettings.deviceTypeSets.find((dts) => dts.name === setQuota.name);
            if (!set) continue; // this would not happen

            let remainingInQuota = setQuota.quantity;
            for (const device of set.devices as DeviceType[]) {
                const used = result[device];
                if (remainingInQuota > used) {
                    result[device] = 0;
                    remainingInQuota -= used;
                } else {
                    result[device] -= remainingInQuota;
                    remainingInQuota = 0;
                }
            }
        }

        for (const limit of options?.withFormDts ?? this.values.deviceTypeSets) {
            if (!limit.value) continue;
            const set = this.dataSource.limitSettings.deviceTypeSets.find((dts) => dts.name === limit.name);
            if (!set) continue; // this would not happen

            let remainingInQuota = limit.value;
            for (const device of set.devices as DeviceType[]) {
                const used = result[device];
                if (remainingInQuota > used) {
                    result[device] = 0;
                    remainingInQuota -= used;
                } else {
                    result[device] -= remainingInQuota;
                    remainingInQuota = 0;
                }
            }
        }

        return result;
    }

    protected override createErrorHelper(errors: Error[]): ErrorHelper {
        return super.createErrorHelper(errors, 1);
    }

    override get hasChanged(): boolean {
        return !deepEqual(this.initialData, this.values);
    }
}
